import { useRef, useState, useEffect, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios'
import { faUserTie, faChair, faFileAlt, faPiggyBank, faChartLine, faGavel, faClipboardList, faBook, faComments, faPrayingHands } from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import Footer from './Footer';

const Reports = () => {
    const server_app_api_php = window.SERVER_APP_API_PHP
    const [report, setReport] = useState([]);
    const [reportDepartment, setReportDepartment] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const departments = [
        { name: 'Director', icon: faUserTie, color: 'from-blue-500 to-cyan-500' },
        { name: 'Chairperson', icon: faChair, color: 'from-purple-500 to-pink-500' },
        { name: 'Secretariat', icon: faFileAlt, color: 'from-emerald-500 to-green-500' },
        { name: 'Treasury', icon: faPiggyBank, color: 'from-amber-500 to-orange-500' },
        { name: 'Planning', icon: faChartLine, color: 'from-indigo-500 to-purple-500' },
        { name: 'Parliamentarian', icon: faGavel, color: 'from-red-500 to-pink-500' },
        { name: 'Projects', icon: faClipboardList, color: 'from-sky-500 to-blue-500' },
        { name: 'Education', icon: faBook, color: 'from-lime-500 to-green-500' },
        { name: 'Communication', icon: faComments, color: 'from-rose-500 to-pink-500' },
        { name: 'Chaplaincy', icon: faPrayingHands, color: 'from-violet-500 to-purple-500' },
    ];

    const viewReports = async (dept) => {
        setIsLoading(true);
        setReportDepartment(dept);
        setIsModalOpen(true);
        
        const formData = new FormData();
        formData.append('apiType', 'get-reports');
        formData.append('department', dept);
        
        try {
            const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setReport(response.data);
        } catch (error) {
            alert('An error occurred while retrieving reports. Please check your internet connection.');
        } finally {
            setIsLoading(false);
        }
    };

    const downloadFile = async (filepath) => {
        try {
            const response = await axios.get(`${server_app_api_php}/server/downloadReports.php?file=${filepath}`, {
                responseType: 'blob'
            });
            
            if (response.status === 200) {
                const blob = new Blob([response.data], { type: 'application/octet-stream' });
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(blob);
                downloadLink.download = filepath.split('/').pop();
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            }
        } catch (error) {
            alert('Error downloading file. Please try again.');
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
            <Header />
            
            <main className="pt-24">
                <div className="max-w-7xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-bold text-gray-800 mb-4">Ministry Reports</h1>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Access reports and documents from various ministry departments. Stay updated with the latest activities and progress.
                        </p>
                    </div>

                    {/* Departments Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                        {departments.map((dept, idx) => (
                            <div 
                                key={idx} 
                                className="bg-white rounded-2xl shadow-lg p-6 transform hover:-translate-y-2 transition-all duration-300 cursor-pointer"
                                onClick={() => viewReports(dept.name)}
                            >
                                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${dept.color} flex items-center justify-center mb-4 mx-auto`}>
                                    <FontAwesomeIcon icon={dept.icon} className="text-white text-2xl" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 text-center mb-2">{dept.name}</h3>
                                <p className="text-gray-500 text-center text-sm mb-4">
                                    View reports and documents from the {dept.name.toLowerCase()} department.
                                </p>
                                <div className="flex justify-center">
                                    <button className="text-blue-600 hover:text-blue-800 font-semibold transition-colors duration-300">
                                        View Reports →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Stats */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-12" hidden>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">10</div>
                                <div className="text-blue-100">Departments</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">24/7</div>
                                <div className="text-blue-100">Access</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">100%</div>
                                <div className="text-blue-100">Secure</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-white mb-2">Updated</div>
                                <div className="text-blue-100">Regularly</div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        {/* Backdrop */}
                        <div 
                            className="fixed inset-0 transition-opacity bg-black bg-opacity-50" 
                            onClick={() => setIsModalOpen(false)}
                        ></div>

                        {/* Modal Content */}
                        <div className="inline-block w-full max-w-6xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-2xl rounded-2xl">
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold text-white">{reportDepartment} Reports</h3>
                                        <p className="text-blue-100 text-sm">View and download department reports</p>
                                    </div>
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="text-white hover:text-gray-200 transition-colors duration-300"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-6">
                                {isLoading ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                                        <p className="text-gray-600">Loading reports...</p>
                                    </div>
                                ) : report.length > 0 ? (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {report.map((item, idx) => (
                                            <div key={idx} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                                                <div className="flex items-center justify-between mb-4">
                                                    <span className="text-sm text-blue-600 font-semibold">
                                                        {new Date(item.created_at).toLocaleDateString()}
                                                    </span>
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                        Report
                                                    </span>
                                                </div>
                                                
                                                <h4 className="font-bold text-gray-800 mb-3 line-clamp-2">{item.title}</h4>
                                                <p className="text-gray-600 text-sm mb-4 line-clamp-3">{item.description}</p>
                                                
                                                {(item.media_urlsImages?.length > 0 || item.media_urlsVideos?.length > 0 || item.media_urlsDocuments?.length > 0) && (
                                                    <div className="space-y-2">
                                                        <p className="text-xs text-gray-500 font-medium">Attachments:</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {item.media_urlsImages?.length > 0 && (
                                                                <button
                                                                    onClick={() => downloadFile(item.media_urlsImages)}
                                                                    className="flex items-center space-x-1 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded-full transition-colors duration-300"
                                                                >
                                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                                                                    </svg>
                                                                    <span>Images ({item.media_urlsImages?.length})</span>
                                                                </button>
                                                            )}
                                                            
                                                            {item.media_urlsVideos?.length > 0 && (
                                                                <button
                                                                    onClick={() => downloadFile(item.media_urlsVideos)}
                                                                    className="flex items-center space-x-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 px-3 py-1 rounded-full transition-colors duration-300"
                                                                >
                                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M10 8v8l5-4-5-4zm9-5H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>
                                                                    </svg>
                                                                    <span>Videos ({item.media_urlsVideos?.length})</span>
                                                                </button>
                                                            )}
                                                            
                                                            {item.media_urlsDocuments?.length > 0 && (
                                                                <button
                                                                    onClick={() => downloadFile(item.media_urlsDocuments)}
                                                                    className="flex items-center space-x-1 text-xs bg-green-50 hover:bg-green-100 text-green-700 px-3 py-1 rounded-full transition-colors duration-300"
                                                                >
                                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                                                    </svg>
                                                                    <span>Documents ({item.media_urlsDocuments?.length})</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                            <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                            </svg>
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-700 mb-2">No Reports Available</h4>
                                        <p className="text-gray-500">There are no reports available for this department at the moment.</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-500">
                                        Showing {report.length} report{report.length !== 1 ? 's' : ''}
                                    </span>
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-2 rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-300"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Reports;